/**
 * The tracker: one script for both properties.
 *
 * It is loaded on every page of the content site and bundled into the app, so
 * two rules hold everywhere in this file:
 *
 *   - it never throws, and it is never awaited on a path the visitor is
 *     waiting on. Every listener is passive; every send is fire-and-forget;
 *     a bug in here must cost a row, never a page;
 *   - it records what people DO — where they look, scroll, click and stall —
 *     and never what they TYPE. Keystrokes are counted, not captured; input
 *     values are masked before a DOM snapshot leaves the browser; text is only
 *     ever taken from buttons and links.
 *
 * What it sends, and where the console shows it:
 *   page view (/v)          overview, journeys, flows, filters
 *   heartbeat (/g)          active time, scroll depth, exits, bot evidence
 *   clicks (/c)             click heatmaps, rage and dead clicks
 *   pointer samples (/m)    attention heatmaps
 *   form touches (/fm)      form drop-off
 *   replay (/r, /rc, /rs)   session replay
 *   events (/e)             custom events, copies, calls to action, outbound links
 *   errors (/x)             the deduplicated error table
 *
 * Consent. In a region that requires it (decided by the browser's timezone,
 * or by the console's setting) nothing identifying is sent until the visitor
 * allows it: page views and behaviour still count, in aggregate, with no
 * cookie and no id. Do Not Track and Global Privacy Control switch the
 * tracker off entirely.
 */

export interface TrackerTexts {
  title: string;
  body: string;
  allow: string;
  essential: string;
  privacy: string;
}

export interface TrackerConfig {
  site: "site" | "app";
  /** where the collector is mounted; nginx proxies it on both hosts */
  collect?: string;
  /** cookie Domain for the consent cookie, so both hosts share the choice */
  cookieDomain?: string;
  /** record sessions (subject to the console's setting and the visitor's consent) */
  replay?: boolean;
  /** extra selectors whose text is masked in snapshots */
  maskSelectors?: string[];
  /** elements that react to clicks without looking like it (so they are not "dead" clicks) */
  interactiveSelectors?: string[];
  /** "auto" asks in consent regions; "always" / "never" override */
  consent?: "auto" | "always" | "never";
  /** the signed-in account's bearer token, so a journey joins its account */
  token?: () => string | null;
  /** the internal search query on this page, if the page is a search results page */
  searchQuery?: () => string | null;
  /** keep Google Analytics' consent state in step (gtag consent mode) */
  gtagConsent?: boolean;
  privacyUrl?: string;
  texts?: Partial<TrackerTexts>;
}

export interface Run {
  ok(result: string, cost?: number): void;
  empty(result?: string): void;
  failed(reason: string): void;
}

export interface Tracker {
  /** a new page view (call on every SPA route change) */
  pageView(path?: string): void;
  /** a browser-only signal: copy, cta, outbound, share, or a custom event the console knows */
  event(name: string, target?: string): void;
  /** a feature run with an outcome */
  action(key: string, input?: string): Run;
  /** mark a form (or a step of one) as submitted, for flows without a real <form> */
  formSubmit(form: string, step?: string): void;
  /**
   * Report an error the code caught itself.
   *
   * The automatic listeners only see what reaches the window, and what they see
   * of a failure inside a web worker is a filename and a column number with no
   * frames. Anything caught in a try/catch should come through here instead, so
   * the log gets the real stack.
   */
  error(message: string, stack?: string): void;
  consent(): "all" | "essential" | null;
  setConsent(choice: "all" | "essential"): void;
  mode(): "full" | "aggregate" | "off";
  pv(): string;
}

declare global {
  interface Window {
    xq?: Tracker;
    gtag?: (...args: unknown[]) => void;
    _phantom?: unknown;
    __nightmare?: unknown;
    callPhantom?: unknown;
    domAutomation?: unknown;
    domAutomationController?: unknown;
  }
}

const NOOP_RUN: Run = { ok() {}, empty() {}, failed() {} };

const CONSENT_KEY = "xq-consent";
const CONSENT_COOKIE = "xqc";
const IDLE_MS = 30_000;
const HEARTBEAT_MS = 15_000;
const MAX_BEACONS = 600;

const TEXTS: Record<"en" | "zh" | "vi", TrackerTexts> = {
  en: {
    title: "Cookies for analytics",
    body: "We set one first-party cookie to understand how this site is used — no advertising, no third parties, nothing leaves our own server. Without it your visit still counts, anonymously.",
    allow: "Allow",
    essential: "Essential only",
    privacy: "How we use it",
  },
  zh: {
    title: "用于统计的 Cookie",
    body: "我们只使用一个第一方 Cookie 来了解本站的使用情况——没有广告，没有第三方，数据不会离开我们自己的服务器。不允许的话，您的访问仍会被匿名计入。",
    allow: "允许",
    essential: "仅必要",
    privacy: "了解详情",
  },
  vi: {
    title: "Cookie dùng để thống kê",
    body: "Chúng tôi chỉ đặt một cookie của chính trang web để hiểu trang được dùng thế nào — không quảng cáo, không bên thứ ba, không dữ liệu nào rời khỏi máy chủ của chúng tôi. Nếu bạn không cho phép, lượt truy cập vẫn được tính, nhưng ẩn danh.",
    allow: "Cho phép",
    essential: "Chỉ cookie thiết yếu",
    privacy: "Cách chúng tôi sử dụng",
  },
};

/** Every European zone plus the Atlantic/Arctic zones of EU/EEA/UK members and Cyprus. Errs wide. */
function consentRegion(tz: string): boolean {
  if (!tz) return false;
  if (tz.indexOf("Europe/") === 0) return true;
  if (tz === "Arctic/Longyearbyen") return true;
  if (/^Atlantic\/(Reykjavik|Faroe|Faeroe|Canary|Madeira|Azores|Jan_Mayen)$/.test(tz)) return true;
  return /^Asia\/(Nicosia|Famagusta)$/.test(tz);
}

function timezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

let seq = 0;
function newId(): string {
  const rnd = Math.random().toString(36).slice(2, 8);
  return `p${Date.now().toString(36)}${(seq++).toString(36)}${rnd}`;
}

/** FNV-1a, twice with different seeds: a 16-hex content key, not a security hash. */
function hash16(s: string): string {
  let a = 0x811c9dc5, b = 0x9747b28c;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x01000193) >>> 0;
  }
  return (a >>> 0).toString(16).padStart(8, "0") + (b >>> 0).toString(16).padStart(8, "0");
}

function readCookie(name: string): string | null {
  try {
    const m = document.cookie.split(";").map((s) => s.trim()).find((s) => s.indexOf(name + "=") === 0);
    return m ? decodeURIComponent(m.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
}

/** The headless-browser signals, as the bitmask the server stores (see HEADLESS_BITS). */
function headlessBits(): number {
  let bits = 0;
  try {
    if (navigator.webdriver) bits |= 1;
    if (!navigator.languages || navigator.languages.length === 0) bits |= 2;
    const ua = navigator.userAgent;
    if (/Chrome\//.test(ua) && !/Mobile|Android/.test(ua) && navigator.plugins && navigator.plugins.length === 0) bits |= 4;
    if (screen.width < 2 || screen.height < 2 || (window.outerWidth === 0 && window.outerHeight === 0)) bits |= 8;
    if (window._phantom || window.__nightmare || window.callPhantom || window.domAutomation || window.domAutomationController || /HeadlessChrome/.test(ua)) bits |= 16;
  } catch {
    /* ignore */
  }
  return bits;
}

const INTERACTIVE = "a,button,input,select,textarea,label,summary,details,option,svg,canvas,video,audio,[role=button],[role=link],[role=tab],[role=menuitem],[role=option],[role=checkbox],[role=switch],[onclick],[tabindex],[contenteditable],[data-cta],[data-track],[data-interactive]";
const TEXT_BEARING = "a,button,summary,label,[role=button],[role=link],[role=tab],input[type=submit],input[type=button]";

function selectorOf(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  let depth = 0;
  while (cur && cur.nodeType === 1 && depth < 5) {
    if (cur.id && /^[A-Za-z][\w-]*$/.test(cur.id)) {
      parts.unshift("#" + cur.id);
      break;
    }
    let s = cur.tagName.toLowerCase();
    if (s === "body" || s === "html") {
      parts.unshift(s);
      break;
    }
    const cls: string[] = [];
    try {
      cur.classList.forEach((c) => { if (cls.length < 2 && /^[A-Za-z][\w-]*$/.test(c)) cls.push(c); });
    } catch {
      /* SVG in old engines */
    }
    if (cls.length) s += "." + cls.join(".");
    const key = cur.getAttribute("data-track") || cur.getAttribute("data-cta") || cur.getAttribute("name");
    if (key) s += `[${key.slice(0, 24)}]`;
    else {
      const parent: HTMLElement | null = cur.parentElement;
      if (parent) {
        const siblings: Element[] = Array.from(parent.children);
        let same = 0, index = 0;
        for (const sib of siblings) {
          if (sib.tagName === cur.tagName) {
            same += 1;
            if (sib === cur) index = same;
          }
        }
        if (same > 1) s += `:nth-of-type(${index})`;
      }
    }
    parts.unshift(s);
    const up: HTMLElement | null = cur.parentElement;
    cur = up;
    depth += 1;
  }
  return parts.join(">").slice(0, 120);
}

function textOf(el: Element): string {
  try {
    const t = el.closest(TEXT_BEARING);
    if (!t) return "";
    const label = t.getAttribute("aria-label") || (t as HTMLElement).innerText || t.textContent || "";
    return label.replace(/\s+/g, " ").trim().slice(0, 40);
  } catch {
    return "";
  }
}

/**
 * Did these clicks select text? A double-click takes a word and a triple-click
 * a paragraph, and the selection belongs to the element that was clicked —
 * which is what tells it apart from a selection made earlier somewhere else.
 */
function selectingText(target: Element): boolean {
  try {
    const sel = document.getSelection();
    if (!sel || sel.isCollapsed || !String(sel).trim()) return false;
    const anchor = sel.anchorNode;
    if (!anchor) return false;
    return target.contains(anchor) || (anchor.parentElement ? target.contains(anchor.parentElement) : false);
  } catch {
    return false;
  }
}

function formKeyOf(el: Element): { form: Element; key: string; step: string | null } | null {
  const form = el.closest("form,[data-form]");
  if (!form) return null;
  const key = form.getAttribute("data-form") || form.id || form.getAttribute("name") || form.getAttribute("aria-label") || "form";
  return { form, key: key.slice(0, 48), step: form.getAttribute("data-step") };
}

function fieldKeyOf(el: Element): string {
  const e = el as HTMLInputElement;
  const k = e.name || e.id || e.getAttribute("aria-label") || e.getAttribute("placeholder") || e.getAttribute("autocomplete") || `${e.tagName.toLowerCase()}:${e.type || ""}`;
  return String(k).slice(0, 48);
}

export function initTracker(cfg: TrackerConfig): Tracker {
  const collect = cfg.collect ?? "/a";
  let mode: "full" | "aggregate" | "off" = "full";
  let sent = 0;
  let pvId = "";
  let pvStart = 0;
  let pvPath = "";
  let first = true;
  const tz = timezone();
  const hs = headlessBits();
  let serverCfg: { replay: boolean; sample: number; mask: string[]; consent: "auto" | "always" | "never" } | null = null;
  const consentMode = () => serverCfg?.consent ?? cfg.consent ?? "auto";

  // ---------------------------------------------------------------- sending

  function post(route: string, body: Record<string, unknown>, opts: { keepalive?: boolean; raw?: BodyInit; headers?: Record<string, string>; want?: boolean } = {}): Promise<Response | null> {
    if (mode === "off") return Promise.resolve(null);
    if (sent >= MAX_BEACONS) return Promise.resolve(null);
    sent += 1;
    try {
      const headers: Record<string, string> = { "Content-Type": opts.raw ? "application/octet-stream" : "application/json", ...(opts.headers ?? {}) };
      const token = cfg.token?.();
      if (token && mode === "full") headers["Authorization"] = `Bearer ${token}`;
      const payload: Record<string, unknown> = { ...body, s: cfg.site };
      if (mode === "aggregate") payload.cm = "agg";
      const p = fetch(collect + route, {
        method: "POST",
        keepalive: opts.keepalive !== false && !opts.raw,
        credentials: "same-origin",
        headers,
        body: opts.raw ?? JSON.stringify(payload),
      });
      return p.then((r) => (opts.want ? r : null)).catch(() => null);
    } catch {
      return Promise.resolve(null);
    }
  }

  // ---------------------------------------------------------------- consent

  function consent(): "all" | "essential" | null {
    try {
      const v = localStorage.getItem(CONSENT_KEY);
      if (v === "all" || v === "essential") return v;
    } catch {
      /* no storage */
    }
    const c = readCookie(CONSENT_COOKIE);
    return c === "1" ? "all" : c === "0" ? "essential" : null;
  }

  function writeConsent(choice: "all" | "essential"): void {
    try { localStorage.setItem(CONSENT_KEY, choice); } catch { /* ignore */ }
    try {
      const secure = location.protocol === "https:" ? "; Secure" : "";
      const domain = cfg.cookieDomain ? `; Domain=${cfg.cookieDomain}` : "";
      document.cookie = `${CONSENT_COOKIE}=${choice === "all" ? "1" : "0"}; Path=/${domain}; Max-Age=${180 * 86400}; SameSite=Lax${secure}`;
    } catch {
      /* ignore */
    }
    if (cfg.gtagConsent !== false) {
      try { window.gtag?.("consent", "update", { analytics_storage: choice === "all" ? "granted" : "denied" }); } catch { /* ignore */ }
    }
  }

  function needsConsent(): boolean {
    const m = consentMode();
    if (m === "never") return false;
    if (m === "always") return true;
    return consentRegion(tz);
  }

  function decideMode(): void {
    try {
      const dnt = navigator.doNotTrack === "1" || (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
      if (dnt) { mode = "off"; return; }
    } catch { /* ignore */ }
    const c = consent();
    if (c === "all") { mode = "full"; return; }
    if (c === "essential") { mode = "aggregate"; return; }
    mode = needsConsent() ? "aggregate" : "full";
  }

  let banner: HTMLElement | null = null;
  function showBanner(): void {
    if (banner || mode === "off" || consent() !== null || !needsConsent()) return;
    if (!document.body) { addEventListener("DOMContentLoaded", showBanner, { once: true }); return; }
    const hl = (document.documentElement.lang || "").toLowerCase();
    const lang = hl.indexOf("zh") === 0 ? "zh" : hl.indexOf("vi") === 0 ? "vi" : "en";
    const t: TrackerTexts = { ...TEXTS[lang], ...(cfg.texts ?? {}) };
    // Dark or light chrome, decided by the page's own background so the card
    // never sits like a white sticker on a dark app.
    let dark = false;
    try {
      const bg = getComputedStyle(document.body).backgroundColor.match(/\d+/g)?.map(Number) ?? [255, 255, 255];
      const html = getComputedStyle(document.documentElement).backgroundColor.match(/\d+/g)?.map(Number) ?? [255, 255, 255];
      const pick = (bg[3] === 0 ? html : bg);
      dark = (0.2126 * pick[0]! + 0.7152 * pick[1]! + 0.0722 * pick[2]!) < 128;
    } catch { /* ignore */ }
    const style = document.createElement("style");
    style.textContent = `
.xqc-wrap{position:fixed;left:16px;bottom:16px;z-index:2147483000;max-width:360px;box-sizing:border-box;padding:16px 18px;border-radius:14px;
  font:14px/1.5 -apple-system,"Segoe UI",system-ui,sans-serif;box-shadow:0 14px 38px rgba(0,0,0,.35);
  background:${dark ? "#1e140c" : "#ffffff"};color:${dark ? "#f3e6c9" : "#16130f"};border:1px solid ${dark ? "rgba(217,180,90,.32)" : "#e7e0d4"}}
.xqc-wrap b{display:block;font-size:15px;margin-bottom:4px}
.xqc-wrap p{margin:0 0 12px;color:${dark ? "#c9b891" : "#3d3730"}}
.xqc-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
.xqc-btn{font:inherit;font-weight:700;padding:8px 14px;border-radius:9px;cursor:pointer;border:1px solid transparent}
.xqc-btn.a{background:${dark ? "#d24334" : "#b3241c"};color:#fff}
.xqc-btn.e{background:transparent;color:inherit;border-color:${dark ? "rgba(217,180,90,.4)" : "#d6cdbd"}}
.xqc-wrap a{margin-left:auto;font-size:13px;color:inherit;opacity:.75;text-decoration:underline}
@media (max-width:480px){.xqc-wrap{left:10px;right:10px;bottom:10px;max-width:none}}`;
    const wrap = document.createElement("div");
    wrap.className = "xqc-wrap";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-label", t.title);
    wrap.setAttribute("data-no-replay", "");
    const title = document.createElement("b");
    title.textContent = t.title;
    const body = document.createElement("p");
    body.textContent = t.body;
    const row = document.createElement("div");
    row.className = "xqc-row";
    const allow = document.createElement("button");
    allow.className = "xqc-btn a";
    allow.type = "button";
    allow.textContent = t.allow;
    const essential = document.createElement("button");
    essential.className = "xqc-btn e";
    essential.type = "button";
    essential.textContent = t.essential;
    row.append(allow, essential);
    if (cfg.privacyUrl) {
      const a = document.createElement("a");
      a.href = cfg.privacyUrl;
      a.textContent = t.privacy;
      row.append(a);
    }
    wrap.append(style, title, body, row);
    document.body.append(wrap);
    banner = wrap;
    allow.addEventListener("click", () => setConsent("all"));
    essential.addEventListener("click", () => setConsent("essential"));
  }

  function setConsent(choice: "all" | "essential"): void {
    writeConsent(choice);
    if (banner) { banner.remove(); banner = null; }
    const before = mode;
    decideMode();
    // Allowed after an aggregate start: begin an identified page view now, so
    // the session starts from the moment of consent rather than being lost.
    if (before !== "full" && mode === "full") startPageView(pvPath || location.pathname, true);
    event("consent", choice);
  }

  // ---------------------------------------------------------------- engagement

  let lastInput = 0;
  let activeMs = 0;
  let lastTick = 0;
  let maxDepth = 0;
  const milestones: (number | null)[] = [null, null, null, null];
  const counters = { mv: 0, sc: 0, ck: 0, kp: 0, tch: 0 };
  let dirty = false;
  let heartbeatTimer = 0;

  function docHeight(): number {
    try {
      const d = document.documentElement, b = document.body;
      return Math.max(d.scrollHeight, b ? b.scrollHeight : 0, d.clientHeight, 1);
    } catch {
      return 1;
    }
  }

  function noteInput(kind: keyof typeof counters): void {
    lastInput = Date.now();
    counters[kind] += 1;
    dirty = true;
  }

  function tick(): void {
    const now = Date.now();
    if (lastTick && document.visibilityState === "visible" && now - lastInput < IDLE_MS) {
      activeMs += Math.min(now - lastTick, 2000);
      dirty = true;
    }
    lastTick = now;
  }

  function measureScroll(): void {
    try {
      const depth = Math.min(100, Math.round(((window.scrollY + window.innerHeight) / docHeight()) * 100));
      if (depth > maxDepth) {
        maxDepth = depth;
        dirty = true;
        const dt = Date.now() - pvStart;
        [25, 50, 75, 100].forEach((m, i) => { if (depth >= m && milestones[i] === null) milestones[i] = dt; });
      }
    } catch {
      /* ignore */
    }
  }

  function heartbeat(end: "hide" | "unload" | "nav" | null = null): void {
    if (!pvId || (!dirty && !end)) return;
    dirty = false;
    void post("/g", {
      pv: pvId, p: pvPath, act: activeMs, tot: Date.now() - pvStart, sd: maxDepth,
      mv: counters.mv, sc: counters.sc, ck: counters.ck, kp: counters.kp, tch: counters.tch,
      dh: docHeight(), vw: window.innerWidth, vh: window.innerHeight, end, sm: milestones,
    });
  }

  // ---------------------------------------------------------------- clicks, moves

  interface PendingClick { row: [number, number, number, string, string, string, 0 | 1, 0 | 1]; t: number; cx: number; cy: number }
  let clicks: PendingClick[] = [];
  /** Click times and positions from the last second, kept across a flush. */
  let recent: { t: number; cx: number; cy: number }[] = [];
  let clickTimer = 0;
  const flushClicks = () => {
    clickTimer = 0;
    if (!clicks.length || !pvId) return;
    const cl = clicks.map((c) => c.row);
    clicks = [];
    void post("/c", { pv: pvId, p: pvPath, vw: window.innerWidth, dh: docHeight(), cl });
  };

  function onClick(e: MouseEvent): void {
    try {
      noteInput("ck");
      const target = e.target as Element | null;
      if (!target || target.nodeType !== 1) return;
      const now = Date.now();
      const x = Math.round((e.clientX / Math.max(1, window.innerWidth)) * 1000);
      const y = Math.round(e.clientY + window.scrollY);
      const row: PendingClick["row"] = [now - pvStart, x, y, selectorOf(target), target.tagName.toLowerCase(), textOf(target), 0, 0];
      const pending: PendingClick = { row, t: now, cx: e.clientX, cy: e.clientY };
      /* Rage: three or more clicks within 700 ms and 24 px of each other —
         unless they just selected text. A triple-click on a paragraph is
         exactly that pattern and is how people select one, so counting it as
         frustration would put a steady drip of false alarm on every page of a
         site made mostly of prose.

         `recent` is kept separately from the pending batch because the batch
         empties when it is sent: a burst that straddles a flush would
         otherwise look like unrelated single clicks. Rows already flushed
         cannot be marked after the fact, but the click in hand still can. */
      recent.push({ t: now, cx: e.clientX, cy: e.clientY });
      while (recent.length && now - recent[0]!.t > 1000) recent.shift();
      const isNear = (c: { t: number; cx: number; cy: number }) => now - c.t < 700 && Math.hypot(c.cx - e.clientX, c.cy - e.clientY) < 24;
      // `recent` already holds this click, so a cluster of three means two others.
      if (recent.filter(isNear).length >= 3 && !selectingText(target)) {
        row[6] = 1;
        for (const c of clicks) if (isNear(c)) c.row[6] = 1;
      }
      clicks.push(pending);
      replayEvent(["c", now - pvStart, Math.round(e.clientX + window.scrollX), y]);
      // Dead: nothing interactive under the pointer, and nothing changed after.
      const interactive = target.closest(INTERACTIVE + (cfg.interactiveSelectors?.length ? "," + cfg.interactiveSelectors.join(",") : ""));
      if (!interactive && typeof MutationObserver !== "undefined") {
        let mutated = false;
        const href = location.href;
        const scrollBefore = window.scrollY;
        const mo = new MutationObserver(() => { mutated = true; });
        mo.observe(document.documentElement, { subtree: true, childList: true, attributes: true, characterData: true });
        setTimeout(() => {
          mo.disconnect();
          const selected = String(document.getSelection() ?? "").length > 0;
          if (!mutated && location.href === href && Math.abs(window.scrollY - scrollBefore) < 4 && !selected) row[7] = 1;
        }, 600);
      }
      if (clicks.length >= 25) flushClicks();
      else if (!clickTimer) clickTimer = window.setTimeout(flushClicks, 3000);
      // Declarative events: data-track="name" and the app's data-cta convention.
      const tracked = target.closest("[data-track]");
      if (tracked) event(tracked.getAttribute("data-track") || "cta", tracked.getAttribute("data-track-target") || textOf(tracked) || "-");
      const a = target.closest("a[href], [data-cta]");
      if (a) {
        const cta = a.getAttribute("data-cta");
        if (cta) event("cta", cta);
        else {
          const href = a.getAttribute("href") || "";
          if (/^https?:/i.test(href)) {
            const host = new URL(href, location.href).hostname;
            if (host !== location.hostname) {
              if (host.indexOf("xiangqimaster.com") >= 0) event("cta", "to-app");
              else event("outbound", host);
            }
          } else if (a.hasAttribute("download") || /\.(pdf|zip|pgn|txt|csv)(\?|$)/i.test(href)) {
            event("download", href.split("/").pop() || "file");
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  let moves: [number, number][] = [];
  let moveTimer = 0;
  let lastMoveSample = 0;
  let lastReplayMove = 0;
  const flushMoves = () => {
    moveTimer = 0;
    if (!moves.length || !pvId) return;
    const pts = moves;
    moves = [];
    void post("/m", { pv: pvId, p: pvPath, vw: window.innerWidth, dh: docHeight(), pts });
  };

  function onMove(e: PointerEvent): void {
    try {
      if (e.pointerType === "touch") return;
      noteInput("mv");
      const now = Date.now();
      if (now - lastMoveSample >= 200) {
        lastMoveSample = now;
        moves.push([Math.round((e.clientX / Math.max(1, window.innerWidth)) * 1000), Math.round(e.clientY + window.scrollY)]);
        if (moves.length >= 60) flushMoves();
        else if (!moveTimer) moveTimer = window.setTimeout(flushMoves, 10_000);
      }
      if (now - lastReplayMove >= 50) {
        lastReplayMove = now;
        replayEvent(["m", now - pvStart, Math.round(e.clientX + window.scrollX), Math.round(e.clientY + window.scrollY)]);
      }
    } catch {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------- forms

  const formBuffers = new Map<string, { step: string | null; ev: [number, string, string][] }>();
  let formTimer = 0;
  const flushForms = () => {
    formTimer = 0;
    if (!pvId) return;
    for (const [f, b] of formBuffers) {
      if (!b.ev.length) continue;
      const ev = b.ev;
      b.ev = [];
      void post("/fm", { pv: pvId, p: pvPath, f, step: b.step, ev });
    }
  };
  function formEvent(form: string, step: string | null, kind: string, field: string): void {
    const key = form;
    let b = formBuffers.get(key);
    if (!b) formBuffers.set(key, (b = { step, ev: [] }));
    b.step = step;
    b.ev.push([Date.now() - pvStart, kind, field]);
    if (b.ev.length >= 40 || kind === "submit") flushForms();
    else if (!formTimer) formTimer = window.setTimeout(flushForms, 5000);
  }
  function onFocusIn(e: FocusEvent): void {
    try {
      const el = e.target as Element | null;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      const f = formKeyOf(el);
      if (f) formEvent(f.key, f.step, "focus", fieldKeyOf(el));
      replayEvent(["f", Date.now() - pvStart, 1]);
    } catch {
      /* ignore */
    }
  }
  function onFocusOut(): void {
    replayEvent(["f", Date.now() - pvStart, 0]);
  }
  function onChange(e: Event): void {
    try {
      const el = e.target as Element | null;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      const f = formKeyOf(el);
      if (f) formEvent(f.key, f.step, "change", fieldKeyOf(el));
    } catch {
      /* ignore */
    }
  }
  function onSubmit(e: Event): void {
    try {
      const el = e.target as Element | null;
      if (!el) return;
      const f = formKeyOf(el);
      if (f) formEvent(f.key, f.step, "submit", "-");
    } catch {
      /* ignore */
    }
  }
  function onSubmitClick(e: MouseEvent): void {
    try {
      const el = (e.target as Element | null)?.closest("[data-submit],button[type=submit],input[type=submit]");
      if (!el) return;
      const f = formKeyOf(el);
      if (f && !(f.form.tagName === "FORM")) formEvent(f.key, f.step, "submit", "-");
    } catch {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------- replay

  let replayOn = false;
  let replayBuf: unknown[][] = [];
  let replaySeq = 0;
  let replayTimer = 0;
  let replayCount = 0;
  const REPLAY_MAX_EVENTS = 12_000;

  function replayWanted(): boolean {
    if (mode !== "full" || cfg.replay === false) return false;
    if (!serverCfg || !serverCfg.replay) return false;
    try {
      let d = sessionStorage.getItem("xq-rs");
      if (d !== "1" && d !== "0") {
        d = Math.random() < serverCfg.sample ? "1" : "0";
        sessionStorage.setItem("xq-rs", d);
      }
      return d === "1";
    } catch {
      return serverCfg.sample >= 1;
    }
  }

  function replayEvent(ev: unknown[]): void {
    if (!replayOn || replayCount >= REPLAY_MAX_EVENTS) return;
    replayCount += 1;
    replayBuf.push(ev);
    if (replayBuf.length >= 400) flushReplay();
    else if (!replayTimer) replayTimer = window.setTimeout(flushReplay, 5000);
  }

  function flushReplay(): void {
    replayTimer = 0;
    if (!replayBuf.length || !pvId) return;
    const ev = replayBuf;
    replayBuf = [];
    void post("/rc", { pv: pvId, seq: replaySeq++, ev });
  }

  function maskTree(root: Element): void {
    const masks = ["[data-mask]", ...(cfg.maskSelectors ?? []), ...(serverCfg?.mask ?? [])];
    for (const sel of masks) {
      let nodes: NodeListOf<Element>;
      try { nodes = root.querySelectorAll(sel); } catch { continue; }
      nodes.forEach((el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        const texts: Text[] = [];
        while (walker.nextNode()) texts.push(walker.currentNode as Text);
        for (const t of texts) if (t.nodeValue && t.nodeValue.trim()) t.nodeValue = t.nodeValue.replace(/\S/g, "▪");
      });
    }
    // Anything typed is never captured; anything that looks like an email is blanked.
    root.querySelectorAll("input,textarea").forEach((el) => {
      const type = (el.getAttribute("type") || "text").toLowerCase();
      if (/^(checkbox|radio|submit|button|reset|range|hidden|color|image)$/.test(type)) return;
      if (el.tagName === "TEXTAREA") el.textContent = "";
      el.setAttribute("value", "");
      el.removeAttribute("data-value");
    });
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const all: Text[] = [];
    while (walker.nextNode()) all.push(walker.currentNode as Text);
    for (const t of all) {
      if (t.nodeValue && /[\w.+-]+@[\w-]+\.[\w.-]+/.test(t.nodeValue)) t.nodeValue = t.nodeValue.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "▪▪▪@▪▪▪");
    }
  }

  function snapshotHtml(): string | null {
    try {
      const root = document.documentElement.cloneNode(true) as HTMLElement;
      root.querySelectorAll("script,noscript,iframe,object,embed,template,[data-no-replay],link[rel~=preload],link[rel~=modulepreload],link[rel~=prefetch]").forEach((n) => n.remove());
      maskTree(root);
      // Relative assets must resolve against the site, not the frame they are shown in.
      root.querySelectorAll("base").forEach((b) => b.remove());
      const base = document.createElement("base");
      base.setAttribute("href", location.origin + "/");
      (root.querySelector("head") ?? root).prepend(base);
      const html = "<!doctype html>" + root.outerHTML;
      return html.length > 3_000_000 ? null : html;
    } catch {
      return null;
    }
  }

  async function gzip(html: string): Promise<{ body: BodyInit; gz: boolean }> {
    try {
      if (typeof CompressionStream === "undefined") return { body: html, gz: false };
      const stream = new Blob([html]).stream().pipeThrough(new CompressionStream("gzip"));
      return { body: await new Response(stream).arrayBuffer(), gz: true };
    } catch {
      return { body: html, gz: false };
    }
  }

  async function startReplay(pv: string): Promise<void> {
    if (!replayWanted()) return;
    replayOn = true;
    replayBuf = [];
    replaySeq = 0;
    replayCount = 0;
    replayEvent(["v", 0, window.innerWidth, window.innerHeight]);
    replayEvent(["s", 0, Math.round(window.scrollX), Math.round(window.scrollY)]);
    // Let the page settle before it is photographed.
    await new Promise<void>((r) => {
      const go = () => setTimeout(r, first ? 600 : 800);
      if (document.readyState === "complete") go();
      else addEventListener("load", go, { once: true });
    });
    if (pvId !== pv || !replayOn) return;
    const html = snapshotHtml();
    if (!html) return;
    const h = hash16(html);
    const res = await post("/r", { pv, p: pvPath, vw: window.innerWidth, vh: window.innerHeight, dh: docHeight(), h, tz }, { want: true, keepalive: false });
    if (!res) return;
    let need = false;
    try { need = !!(await res.json()).need; } catch { need = false; }
    if (!need) return;
    const { body, gz } = await gzip(html);
    void post(`/rs?h=${h}`, {}, { raw: body, keepalive: false, headers: gz ? { "X-Encoding": "gzip" } : {} });
  }

  // ---------------------------------------------------------------- page views

  function endPageView(end: "hide" | "unload" | "nav"): void {
    if (!pvId) return;
    tick();
    heartbeat(end);
    flushClicks();
    flushMoves();
    flushForms();
    flushReplay();
  }

  function startPageView(path: string, afterConsent = false): void {
    if (mode === "off") return;
    if (pvId) endPageView("nav");
    pvId = newId();
    pvStart = Date.now();
    pvPath = path;
    activeMs = 0;
    lastTick = pvStart;
    lastInput = pvStart; // the navigation itself is an input
    maxDepth = 0;
    for (let i = 0; i < 4; i++) milestones[i] = null;
    counters.mv = counters.sc = counters.ck = counters.kp = counters.tch = 0;
    dirty = false;
    replayOn = false;
    formBuffers.clear();
    let nav: string | null = null;
    try { nav = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? null; } catch { /* ignore */ }
    let fromTag: string | null = null;
    let lq = "";
    try {
      const params = new URLSearchParams(location.search);
      fromTag = params.get("from");
      if (first) lq = location.search;
    } catch { /* ignore */ }
    let q: string | null = null;
    try { q = cfg.searchQuery?.() ?? null; } catch { q = null; }
    void post("/v", {
      pv: pvId, p: path, r: first ? document.referrer || "" : "", from: fromTag, tz, lang: navigator.language || null,
      vw: window.innerWidth, vh: window.innerHeight, wd: !!navigator.webdriver, hs, q, nav: first || afterConsent ? nav : "navigate", lq,
    });
    first = false;
    setTimeout(measureScroll, 250);
    void startReplay(pvId);
  }

  // ---------------------------------------------------------------- events, actions, errors

  function event(name: string, target = "-"): void {
    if (!name) return;
    void post("/e", { n: name, k: target, p: pvPath || location.pathname, pv: pvId || undefined });
  }

  function action(key: string, input?: string): Run {
    if (mode !== "full" || sent >= MAX_BEACONS) return NOOP_RUN;
    const id = newId();
    const started = Date.now();
    let done = false;
    void post("/f", { k: key, id, i: input ?? null, p: pvPath || location.pathname, pv: pvId || undefined });
    const finish = (st: "ok" | "empty" | "error", r: string | null, c?: number) => {
      if (done) return;
      done = true;
      void post("/fr", { id, st, r, ms: Date.now() - started, ...(c == null ? {} : { c }) });
    };
    return {
      ok: (result, cost) => finish("ok", result, cost),
      empty: (result = "nothing useful") => finish("empty", result),
      failed: (reason) => finish("error", reason),
    };
  }

  // Hoisted out of wireErrors so a caught error can be reported by hand through
  // the same path, and share the same cap -- a loop that throws every frame must
  // not turn into a beacon flood.
  let errorsSent = 0;
  const report = (message: string, stack: string) => {
    if (errorsSent >= 8) return;
    errorsSent += 1;
    void post("/x", {
      m: String(message).slice(0, 300),
      st: String(stack || "").slice(0, 1200),
      p: pvPath || location.pathname,
      // Which build threw it. The first question about any error is "which
      // version saw this", and after a deploy it is the difference between a
      // live bug and a tab somebody left open across the release. Set by the
      // host that loads this tracker; absent is fine.
      b: String((globalThis as { __XQ_BUILD__?: string }).__XQ_BUILD__ || "").slice(0, 40),
    });
  };

  function wireErrors(): void {
    addEventListener("error", (e) => {
      const err = e.error as Error | undefined;
      report(err?.message || e.message || "error", err?.stack || `${e.filename}:${e.lineno}:${e.colno}`);
    });
    addEventListener("unhandledrejection", (e) => {
      const r = e.reason as { message?: string; stack?: string } | string | undefined;
      report(typeof r === "string" ? r : r?.message || "unhandled rejection", (typeof r === "object" && r?.stack) || "");
    });
  }

  function wireCopy(): void {
    document.addEventListener("copy", () => {
      try {
        const sel = String(document.getSelection() ?? "").trim();
        if (!sel) return;
        const target = document.activeElement?.closest?.("[data-copy]")?.getAttribute("data-copy");
        event("copy", target || (sel.length > 60 ? "passage" : "snippet"));
      } catch {
        /* ignore */
      }
    }, { passive: true, capture: true });
  }

  // ---------------------------------------------------------------- boot

  function boot(): void {
    decideMode();
    if (mode === "off") return;
    const passive = { passive: true, capture: true } as const;
    document.addEventListener("pointermove", onMove, passive);
    document.addEventListener("click", onClick, passive);
    document.addEventListener("click", onSubmitClick, passive);
    document.addEventListener("keydown", () => noteInput("kp"), passive);
    document.addEventListener("touchstart", () => noteInput("tch"), passive);
    document.addEventListener("wheel", () => { lastInput = Date.now(); }, passive);
    addEventListener("scroll", () => {
      noteInput("sc");
      measureScroll();
      const now = Date.now();
      replayEvent(["s", now - pvStart, Math.round(window.scrollX), Math.round(window.scrollY)]);
    }, { passive: true });
    addEventListener("resize", () => { measureScroll(); replayEvent(["v", Date.now() - pvStart, window.innerWidth, window.innerHeight]); }, { passive: true });
    document.addEventListener("focusin", onFocusIn, passive);
    document.addEventListener("focusout", onFocusOut, passive);
    document.addEventListener("change", onChange, passive);
    document.addEventListener("submit", onSubmit, passive);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        endPageView("hide");
      } else {
        lastTick = Date.now();
        replayEvent(["h", Date.now() - pvStart, 0]);
      }
    });
    addEventListener("pagehide", () => endPageView("unload"));
    heartbeatTimer = window.setInterval(() => { tick(); heartbeat(); }, HEARTBEAT_MS);
    window.setInterval(tick, 1000);
    void heartbeatTimer;
    wireErrors();
    wireCopy();

    // The console's settings: whether to record, what to mask, whether to ask.
    fetch(collect + "/cfg", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((c: typeof serverCfg) => {
        serverCfg = c && typeof c === "object" ? c : { replay: false, sample: 0, mask: [], consent: "auto" };
        const before = mode;
        decideMode();
        if (before !== mode && pvId) startPageView(pvPath, true);
        showBanner();
        if (pvId && mode === "full" && !replayOn) void startReplay(pvId);
      })
      .catch(() => {
        serverCfg = { replay: false, sample: 0, mask: [], consent: "auto" };
        showBanner();
      });

    startPageView(location.pathname);
  }

  const api: Tracker = {
    pageView: (path) => { try { startPageView(path ?? location.pathname); } catch { /* ignore */ } },
    event: (name, target) => { try { event(name, target); } catch { /* ignore */ } },
    action: (key, input) => { try { return action(key, input); } catch { return NOOP_RUN; } },
    formSubmit: (form, step) => { try { formEvent(form.slice(0, 48), step ?? null, "submit", "-"); } catch { /* ignore */ } },
    error: (message, stack) => { try { report(message, stack ?? ""); } catch { /* ignore */ } },
    consent,
    setConsent: (choice) => { try { setConsent(choice); } catch { /* ignore */ } },
    mode: () => mode,
    pv: () => pvId,
  };

  try {
    boot();
  } catch {
    /* a browser that cannot do this still gets a working page */
  }
  try { window.xq = api; } catch { /* ignore */ }
  return api;
}
